import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { equityMarketDataFetcher } from '@/libs/fetcher/presets'
import type { StockRatings, StockRatingType } from '@/types/stock'

interface StockRatingsResponse {
  asset_id: string
  buy_percentage: string
  hold_percentage: string
  sell_percentage: string
  target_price: string
  estimated_return_percentage: string
  rating: StockRatingType
}

const transformToStockRatings = (data: StockRatingsResponse | null): StockRatings => {
  if (!data) {
    return {
      asset_id: '',
      buy: 0,
      hold: 0,
      sell: 0,
      target_price: 0,
      est_return: 0,
      rating: 'HOLD',
    }
  }

  return {
    asset_id: data.asset_id,
    buy: parseFloat(data.buy_percentage) || 0,
    hold: parseFloat(data.hold_percentage) || 0,
    sell: parseFloat(data.sell_percentage) || 0,
    target_price: parseFloat(data.target_price) || 0,
    est_return: parseFloat(data.estimated_return_percentage) || 0,
    rating: data.rating,
  }
}

const fetchStockRatingsById = async (assetId: string | number): Promise<StockRatings> => {
  const data = await equityMarketDataFetcher<StockRatingsResponse>(
    `/ratings/${assetId}`,
    {},
    {
      forceResponseType: 'json',
    },
  )

  return transformToStockRatings(data)
}

export const stockRatingsByIdQueryOptions = (assetId: string | number) =>
  queryOptions({
    queryKey: ['stockRatingsById', assetId],
    queryFn: () => fetchStockRatingsById(assetId),
  })

export const useStockRatingsById = (assetId: string | number) =>
  useSuspenseQuery(stockRatingsByIdQueryOptions(assetId))
